import { Fragment } from "react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";

import {
    IContextClassifierPageRange,
    IContextClassifierClassPageRange
} from "../lib/types";

import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { IntegerInput } from "./Textbox";

type PageRangeClassifierProps = {
    classifier: IContextClassifierPageRange;
    isDisabled: boolean;
    onAddClass: () => void;
    onUpdateClass: (class_idx: number, updates: Partial<IContextClassifierClassPageRange>) => void;
    onDeleteClass: (class_idx: number) => void;
}

export function PageRangeClassifier({
    classifier,
    isDisabled,
    onAddClass,
    onUpdateClass,
    onDeleteClass
}: PageRangeClassifierProps) {

    const updatePageRange = (class_idx: number, position: "start" | "end", updates: { type: "absolute" | "from_last", page_idx?: number, offset?: number }) => {
        const new_page_range = { ...classifier.classes[class_idx].page_range };

        if (updates.type === "absolute") {
            new_page_range[position] = { type: "absolute", page_idx: updates.page_idx || 0 };
        } else {
            new_page_range[position] = { type: "from_last", offset: updates.offset || 0 };
        }

        onUpdateClass(class_idx, { page_range: new_page_range });
    };

    const getTooltipContent = (class_item: IContextClassifierClassPageRange) => {
        const start_instruction = class_item.page_range.start.type === "absolute" ? "Page index (0-based)" : "Offset from last page";
        const end_instruction = class_item.page_range.end.type === "absolute" ? "Page index (0-based)" : "Offset from last page";

        return `<div class="max-w-sm space-y-2">
            <div>
                <strong>Start Position:</strong> ${start_instruction}
            </div>
            <div>
                <strong>End Position:</strong> ${end_instruction}
            </div>
            <div>
                <strong>Examples:</strong>
                <div>• First 5 pages: Start = From Beginning (0), End = From Beginning (4)</div>
                <div>• Last 5 pages: Start = From End (4), End = From End (0)</div>
                <div>• All except first and last: Start = From Beginning (1), End = From End (1)</div>
            </div>
        </div>`;
    };

    return (
        <Fragment>
            <div className="space-y-2">
                <label className="block text-sm font-medium">Labels</label>
                {classifier.classes.map((class_item, class_idx) => (
                    <div key={class_idx} className="border rounded bg-gray-50 p-3 space-y-3">
                        <div className="flex gap-2 items-center">
                            <h4 className="text-sm font-medium whitespace-nowrap">Label {class_idx + 1}</h4>
                            <input
                                type="text"
                                placeholder="Class name"
                                value={class_item.name}
                                disabled={isDisabled}
                                onChange={(e) => onUpdateClass(class_idx, { name: e.target.value })}
                                className="block w-full rounded-md border-0 py-1.5 px-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-space_blue-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <Button
                                icon={TrashIcon}
                                disabled={isDisabled}
                                onClick={() => onDeleteClass(class_idx)}
                            />
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center gap-1">
                                <h5 className="text-sm font-medium">Page Range Configuration</h5>
                                <QuestionMarkCircleIcon
                                    className="w-4 h-4 text-gray-400"
                                    data-tooltip-id={`page-range-tooltip-${class_idx}`}
                                    data-tooltip-html={getTooltipContent(class_item)}
                                />
                                <Tooltip id={`page-range-tooltip-${class_idx}`} />
                            </div>

                            {/* Start Position */}
                            <div className="space-y-2">
                                <label className="block text-xs font-medium text-gray-600">Start Position</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <Dropdown
                                        values={["From Beginning", "From End"]}
                                        ids={["absolute", "from_last"]}
                                        selected={class_item.page_range.start.type}
                                        disabled={isDisabled}
                                        onChange={(value) => updatePageRange(class_idx, "start", {
                                            type: value as "absolute" | "from_last",
                                            page_idx: value === "absolute" ? (class_item.page_range.start.type === "absolute" ? class_item.page_range.start.page_idx : 0) : undefined,
                                            offset: value === "from_last" ? (class_item.page_range.start.type === "from_last" ? class_item.page_range.start.offset : 0) : undefined
                                        })}
                                    />
                                    <div className="col-span-2">
                                        <IntegerInput
                                            value={class_item.page_range.start.type === "absolute"
                                                ? class_item.page_range.start.page_idx
                                                : class_item.page_range.start.offset}
                                            disabled={isDisabled}
                                            onChange={(value) => updatePageRange(class_idx, "start", {
                                                type: class_item.page_range.start.type,
                                                page_idx: class_item.page_range.start.type === "absolute" ? value : undefined,
                                                offset: class_item.page_range.start.type === "from_last" ? value : undefined
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* End Position */}
                            <div className="space-y-2">
                                <label className="block text-xs font-medium text-gray-600">End Position</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <Dropdown
                                        values={["From Beginning", "From End"]}
                                        ids={["absolute", "from_last"]}
                                        selected={class_item.page_range.end.type}
                                        disabled={isDisabled}
                                        onChange={(value) => updatePageRange(class_idx, "end", {
                                            type: value as "absolute" | "from_last",
                                            page_idx: value === "absolute" ? (class_item.page_range.end.type === "absolute" ? class_item.page_range.end.page_idx : 0) : undefined,
                                            offset: value === "from_last" ? (class_item.page_range.end.type === "from_last" ? class_item.page_range.end.offset : 0) : undefined
                                        })}
                                    />
                                    <div className="col-span-2">
                                        <IntegerInput
                                            value={class_item.page_range.end.type === "absolute"
                                                ? class_item.page_range.end.page_idx
                                                : class_item.page_range.end.offset}
                                            disabled={isDisabled}
                                            onChange={(value) => updatePageRange(class_idx, "end", {
                                                type: class_item.page_range.end.type,
                                                page_idx: class_item.page_range.end.type === "absolute" ? value : undefined,
                                                offset: class_item.page_range.end.type === "from_last" ? value : undefined
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="flex justify-end items-center">
                    <Button
                        text="Add Label"
                        icon={PlusIcon}
                        disabled={isDisabled}
                        onClick={onAddClass}
                    />
                </div>
            </div>
        </Fragment>
    );
}